import {
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { IdeaEvidence } from '@models/Sessions/Idea/evidence';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { Editor, generateText, JSONContent } from '@tiptap/core';
import { Bold } from '@tiptap/extension-bold';
import { BubbleMenuPluginProps } from '@tiptap/extension-bubble-menu';
import { BulletList } from '@tiptap/extension-bullet-list';
import { Collaboration } from '@tiptap/extension-collaboration';
import { Document } from '@tiptap/extension-document';
import { Heading } from '@tiptap/extension-heading';
import Highlight from '@tiptap/extension-highlight';
import ListItem from '@tiptap/extension-list-item';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Text } from '@tiptap/extension-text';
import { NgxTiptapModule } from 'ngx-tiptap';
import { Markdown } from 'tiptap-markdown';
import * as Y from 'yjs';

import { NoopValueAccessorDirective } from '../../../directives/noop-value-accessor.directive';
import { injectNgControl } from '../../../utils/inject-ng-control';
import { DeletionMark, InsertionMark } from '../extensions/track-changes';
import { TiptapToolbarComponent } from '../tiptap-toolbar/tiptap-toolbar.component';

@Component({
  selector: 'app-tiptap-editor',
  standalone: true,
  hostDirectives: [NoopValueAccessorDirective],
  imports: [
    ReactiveFormsModule,
    NgxTiptapModule,
    TiptapToolbarComponent,
    NgbPopoverModule,
  ],
  templateUrl: './tiptap-editor.component.html',
  styleUrl: './tiptap-editor.component.scss',
  host: {
    class: 'd-flex flex-column gap-2',
  },
})
export class TiptapEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() showToolbar = true;
  @Input() footnotes: IdeaEvidence[] = [];
  @Input() shouldUnsetHighlight = true;

  @Output() undo = new EventEmitter<void>();
  @Output() redo = new EventEmitter<void>();

  readonly editor: Editor;
  trackChangeEnabled = false;
  currentUser = null;
  readonly bubbleMenuTippyOptions: BubbleMenuPluginProps['tippyOptions'] = {
    onShow: () => {
      this.editor.commands.setHighlight();
    },
    onHide: () => {
      if (this.shouldUnsetHighlight) {
        this.unsetHighlight();
      }
    },
  };

  readonly ngControl = injectNgControl();
  readonly injector = inject(Injector);

  constructor() {
    this.editor = new Editor({
      extensions: [
        Document,
        Collaboration.configure({
          document: new Y.Doc(),
        }),
        Heading.configure({
          levels: [1, 2, 3],
        }),
        Markdown,
        Paragraph,
        Text,
        InsertionMark,
        DeletionMark,
        Bold,
        // Footnotes(this.injector).configure({
        //   getFootnote: index => this.footnotes?.find(f => f.citation === index),
        // }),
        BulletList,
        ListItem,
        Highlight,
        Placeholder,
      ],
    });
  }

  generateText(content: JSONContent) {
    return generateText(content, this.editor.extensionManager.extensions);
  }

  getSelectionAndHighlight() {
    this.editor.commands.setHighlight();
    return this.getSelectionAndReset();
  }

  getSelectionAndReset() {
    const text = this.getSelection();
    this.editor.commands.setTextSelection(0);
    return text;
  }

  getSelection() {
    const text = this.editor.state.doc.textBetween(
      this.editor.state.selection.from,
      this.editor.state.selection.to,
      ' ',
    );
    return text;
  }

  replaceSelection(newText: string) {
    this.editor.view.dispatch(
      this.editor.state.tr.insertText(
        newText,
        this.editor.state.selection.from,
        this.editor.state.selection.to,
      ),
    );
  }

  unsetHighlight() {
    const { from, to } = this.editor.state.selection;

    this.editor
      .chain()
      .selectAll()
      .unsetHighlight()
      .setTextSelection({ from, to })
      .run();
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnInit(): void {
    // this.editor.setOptions();
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method
  ngOnChanges(): void {
    // this.editor.setOptions();
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }
}
